import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Menu, X, LogIn, ShoppingBag } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

// Priority controls what shows in the cramped tablet range (md to lg).
// - "primary": visible from md and up (≥ 768px)
// - "secondary": only shown at lg and up (≥ 1024px), to avoid collisions
//   between the logo, nav, and CTA group on tablet widths.
const navLinks = [
  { label: "Shop", href: "/shop", isRoute: true, priority: "primary" },
  { label: "Book IV", href: "/iv", isRoute: true, priority: "primary" },
  { label: "Peptides", href: "#peptides", priority: "primary" },
  { label: "Quiz", href: "#quiz", priority: "primary" },
  { label: "Benefits", href: "#benefits", priority: "secondary" },
  { label: "Articles", href: "/blog", isRoute: true, priority: "secondary" },
  { label: "Testimonials", href: "#testimonials", priority: "secondary" },
  { label: "FAQ", href: "#faq", priority: "secondary" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Lock body scroll while the slide-down panel is open so the
  // background page doesn't scroll under the user's finger on mobile.
  useEffect(() => {
    if (mobileOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [mobileOpen]);

  const scrollTo = (href) => {
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const handleNav = (link) => {
    setMobileOpen(false);
    if (link.isRoute) navigate(link.href);
    else scrollTo(link.href);
  };

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled || mobileOpen ? "glass-header shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-12 flex items-center justify-between h-16 md:h-20">
        {/* Logo */}
        <a
          href="#"
          data-testid="logo"
          className="font-serif text-lg md:text-xl lg:text-2xl font-light tracking-tight text-[#2A3324] shrink-0"
          onClick={(e) => {
            e.preventDefault();
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
        >
          peptides<span className="font-semibold">recovery</span>
        </a>

        {/* Desktop Nav — compact at md, full at lg+ */}
        <nav className="hidden md:flex items-center gap-5 lg:gap-8 min-w-0">
          {navLinks.map((link) => (
            <button
              key={link.href}
              data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => handleNav(link)}
              className={`text-sm font-sans font-medium tracking-wide text-[#2A3324]/70 hover:text-[#2A3324] transition-colors duration-300 whitespace-nowrap ${
                link.priority === "secondary" ? "hidden lg:inline-flex" : ""
              }`}
            >
              {link.label}
            </button>
          ))}
        </nav>

        {/* CTA Group — compact at md, full at lg+ */}
        <div className="hidden md:flex items-center gap-2 lg:gap-3 shrink-0">
          <button
            data-testid="header-login-btn"
            onClick={() => navigate("/admin/login")}
            aria-label="Log In"
            className="hidden lg:inline-flex items-center gap-1.5 text-sm font-sans font-medium tracking-wide text-[#2A3324]/60 hover:text-[#2A3324] transition-colors duration-300"
          >
            <LogIn size={16} strokeWidth={1.5} />
            Log In
          </button>
          {/* Icon-only Login at md range to save space */}
          <button
            data-testid="header-login-icon-btn"
            onClick={() => navigate("/admin/login")}
            aria-label="Log In"
            className="lg:hidden inline-flex items-center justify-center h-9 w-9 rounded-full text-[#2A3324]/60 hover:text-[#2A3324] hover:bg-[#2A3324]/5 transition-colors duration-300"
          >
            <LogIn size={16} strokeWidth={1.5} />
          </button>
          <button
            data-testid="header-shop-btn"
            onClick={() => navigate("/shop")}
            className="hidden lg:inline-flex items-center gap-1.5 bg-[#2A3324] text-[#F7F5F0] hover:bg-[#1F2618] rounded-full px-4 lg:px-5 py-2 lg:py-2.5 text-sm font-sans font-medium tracking-wide transition-all duration-300 hover:-translate-y-0.5 whitespace-nowrap"
          >
            <ShoppingBag size={15} strokeWidth={1.75} />
            Shop
          </button>
          <a
            href="#quiz"
            data-testid="header-quiz-btn"
            onClick={(e) => {
              e.preventDefault();
              scrollTo("#quiz");
            }}
            className="bg-[#5C715E] text-[#F7F5F0] hover:bg-[#4A5A4B] rounded-full px-5 lg:px-7 py-2 lg:py-3 text-sm font-sans font-medium tracking-wide transition-all duration-300 hover:-translate-y-0.5 whitespace-nowrap"
          >
            Take the Quiz
          </a>
        </div>

        {/* Mobile Toggle */}
        <button
          data-testid="mobile-menu-toggle"
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
          aria-expanded={mobileOpen}
          className="md:hidden inline-flex items-center justify-center h-10 w-10 rounded-full text-[#2A3324] hover:bg-[#2A3324]/5 transition-colors"
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile slide-down panel */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            data-testid="mobile-menu-panel"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden glass-header border-t border-[#2A3324]/5 max-h-[calc(100vh-4rem)] overflow-y-auto"
          >
            <div className="px-5 py-5 flex flex-col gap-1.5">
              {navLinks.map((link) => (
                <button
                  key={link.href}
                  data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => handleNav(link)}
                  className="text-left text-base font-sans font-medium text-[#2A3324]/80 hover:text-[#2A3324] py-2"
                >
                  {link.label}
                </button>
              ))}
              <div className="h-px bg-[#2A3324]/10 my-2" />
              <button
                data-testid="mobile-login-btn"
                onClick={() => { setMobileOpen(false); navigate("/admin/login"); }}
                className="text-left text-base font-sans font-medium text-[#2A3324]/70 hover:text-[#2A3324] py-2 flex items-center gap-2"
              >
                <LogIn size={16} /> Patient Login
              </button>
              <button
                data-testid="mobile-shop-btn"
                onClick={() => { setMobileOpen(false); navigate("/shop"); }}
                className="mt-2 bg-[#2A3324] text-[#F7F5F0] rounded-full px-6 py-3 text-sm font-sans font-medium w-full flex items-center justify-center gap-2"
              >
                <ShoppingBag size={15} /> Shop now
              </button>
              <button
                data-testid="mobile-quiz-btn"
                onClick={() => scrollTo("#quiz")}
                className="bg-[#5C715E] text-[#F7F5F0] rounded-full px-6 py-3 text-sm font-sans font-medium w-full"
              >
                Take the Quiz
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
